import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import SpeedIcon from '@mui/icons-material/Speed';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useTheme } from '@mui/material/styles';
import GaugeChart from 'react-gauge-chart';
import './ProfitFactorGauge.css';
import { usePeriod } from './PeriodContext';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import { CircularProgress } from '@mui/material';

Date.prototype.getWeek = function () {
  const date = new Date(this.getTime());
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 4 - (date.getDay() || 7));
  const yearStart = new Date(date.getFullYear(), 0, 1);
  const weekNumber = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
  return weekNumber;
};

const ProfitFactorGauge = ({ userID, trades }) => {
  const { selectedPeriod, selectedYear, selectedMonth, showMissedTrades, selectedTag } = usePeriod();
  const [journalEntries, setJournalEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

  useEffect(() => {
    const processTrades = () => {
      setLoading(true);
      const filteredEntries = trades.filter(entry => entry.status === (showMissedTrades ? 'Missed' : 'Closed'));
      setJournalEntries(filteredEntries);
      setHasData(filteredEntries.length > 0);
      setLoading(false);
    };

    processTrades();
  }, [userID, trades, showMissedTrades]);

  const filterEntries = (entries) => {
    const matchesPeriod = (entryDate) => {
      if (selectedPeriod === 'today') {
        return entryDate.toDateString() === new Date().toDateString();
      } else if (selectedPeriod === 'this-week') {
        return entryDate.getWeek() === new Date().getWeek() && entryDate.getFullYear() === new Date().getFullYear();
      } else if (selectedPeriod === 'this-month') {
        return entryDate.getMonth() === new Date().getMonth() && entryDate.getFullYear() === new Date().getFullYear();
      } else if (selectedPeriod === 'this-year') {
        return entryDate.getFullYear() === new Date().getFullYear();
      } else if (selectedPeriod === 'pick-month') {
        return entryDate.getMonth() + 1 === selectedMonth && entryDate.getFullYear() === selectedYear;
      } else if (selectedPeriod === 'pick-year') {
        return entryDate.getFullYear() === selectedYear;
      } else if (selectedPeriod === 'pick-month-year') {
        return entryDate.getMonth() + 1 === selectedMonth && entryDate.getFullYear() === selectedYear;
      } else {
        return true; // all-time
      }
    };

    return entries.filter(entry => {
      const matchesTag = selectedTag === 'all' || 
        (Array.isArray(entry.tags) && entry.tags.includes(selectedTag));
      return matchesPeriod(new Date(entry.date)) && matchesTag;
    });
  };

  const calculateProfitFactor = () => {
    let totalWins = 0;
    let totalLosses = 0;

    filterEntries(journalEntries).forEach(entry => {
      const returns = parseFloat(entry.returns);
      if (returns > 0) {
        totalWins += returns;
      } else if (returns < 0) {
        totalLosses += Math.abs(returns);
      }
    });

    if (totalLosses === 0 && totalWins === 0) {
      return 0; // No trades
    } else if (totalLosses === 0) {
      return 5; // All trades are wins, cap at upper limit
    }

    const rawProfitFactor = totalWins / totalLosses;
    return Math.min(rawProfitFactor, 5); // Cap at 5 for display
  };

  const profitFactor = useMemo(calculateProfitFactor, [journalEntries, selectedPeriod, selectedYear, selectedMonth, showMissedTrades, selectedTag]);

  const upperLimit = 5;
  const gaugeValue = Math.min(profitFactor / upperLimit, 1);
  const profitFactorColor = profitFactor >= 1 ? 'success' : 'error';

  const tooltipContent = `Profit Factor measures trading effectiveness for ${showMissedTrades ? 'missed' : 'closed'} trades.\n\nCalculated as total profits divided by total losses.\n\nBelow 1: Losing strategy.\n1-2: Decent performance.\n2-3: Good performance.\n3+: Excellent performance.\n\nCapped at 5 for display.`;

  const renderContent = () => {
    if (loading) {
      return (
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </MDBox>
      );
    }

    if (!hasData) {
      return (
        <MDBox 
          display="flex" 
          flexDirection="column" 
          justifyContent="center" 
          alignItems="center" 
          minHeight="200px"
          textAlign="center"
          px={2}
        >
          <InfoIcon sx={{ fontSize: 40, mb: 2, color: iconColor }} />
          <MDTypography variant="h6" color="text">
            No trading data available yet
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Complete some trades to see your performance charts
          </MDTypography>
        </MDBox>
      );
    }

    return (
      <MDBox
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="200px"
        px={2}
        py={1}
      >
        <MDBox
          sx={{
            width: '100%',
            maxWidth: '250px', // Constrain gauge size
            position: 'relative',
          }}
        >
          <GaugeChart 
            id="profit-factor-gauge"
            nrOfLevels={20}
            colors={[theme.palette.error.main, theme.palette.warning.main, theme.palette.success.main]}
            arcWidth={0.25}
            percent={gaugeValue}
            hideText={true}
            needleColor={theme.palette.text.secondary}
            needleBaseColor={theme.palette[profitFactorColor].main}
            style={{ width: '100%', height: '80px' }}
            animate={false}
          />
        </MDBox>
        <MDBox display="flex" justifyContent="space-between" width="100%" maxWidth="200px" mt={3}>
          <MDBox display="flex" flexDirection="column" alignItems="flex-start">
            <MDTypography
              variant="caption"
              fontWeight="medium"
              textTransform="uppercase"
              color="error"
              sx={{ letterSpacing: '1px' }}
            >
              Poor
            </MDTypography>
            <MDTypography variant="caption" color="text" fontWeight="light">
              0
            </MDTypography>
          </MDBox>
          <MDBox display="flex" flexDirection="column" alignItems="flex-end">
            <MDTypography
              variant="caption"
              fontWeight="medium"
              textTransform="uppercase"
              color="success"
              sx={{ letterSpacing: '1px' }}
            >
              Excellent
            </MDTypography>
            <MDTypography variant="caption" color="text" fontWeight="light">
              5
            </MDTypography>
          </MDBox>
        </MDBox>
        <MDTypography
          component="span"
          variant="button"
          fontWeight="bold"
          fontFamily="inherit"
          color={profitFactorColor}
          mt={1}
          sx={{ fontSize: '1.1rem' }}
        >
          {profitFactor.toFixed(2)}
        </MDTypography>
      </MDBox>
    );
  };

  return (
    <Card style={{ minHeight: '300px' }} className="profit-factor-card">
      <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
        <MDBox display="flex" justifyContent="space-between" alignItems="center">
          <MDBox
            variant="gradient"
            bgColor="light"
            color="dark"
            coloredShadow="light"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-5}
          >
            <SpeedIcon fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25} className="profit-factor-label">
            <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
              Profit Factor {showMissedTrades ? '(Missed)' : ''}
            </MDTypography>
            <Tooltip 
              title={tooltipContent}
              placement="bottom-end"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                    p: 1.5,
                    maxWidth: '250px',
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    '& .MuiTooltip-arrow': {
                      color: theme.palette.background.paper,
                    }
                  }
                }
              }}
            >
              <InfoIcon 
                sx={{ 
                  ml: 1, 
                  fontSize: '1rem',
                  color: iconColor,
                  cursor: 'help'
                }} 
              />
            </Tooltip>
            <MDTypography variant="h4" fontFamily="inherit" style={{ visibility: 'hidden' }}>0</MDTypography>
          </MDBox>
        </MDBox>
        <Divider />
        {renderContent()}
      </MDBox>
    </Card>
  );
};

ProfitFactorGauge.propTypes = {
  userID: PropTypes.string.isRequired,
  trades: PropTypes.array.isRequired,
};

export default ProfitFactorGauge;